// SPDX-FileCopyrightText: 2026 ParzLabs and Diffclaw contributors
// SPDX-License-Identifier: AGPL-3.0-only

import { useEffect, useMemo, useState } from "react";

import {
  ArrowRight,
  CircleAlert,
  Inbox,
  RefreshCw,
  Search,
} from "lucide-react";

import type { CockpitApi, CockpitReviewListItem } from "./api.js";
import {
  displayReviewRunState,
  pluralize,
  relativeTime,
  reviewDescription,
  reviewReference,
  reviewTitle,
  shortIdentifier,
  stateTone,
} from "./presentation.js";

type QueueFilter = "all" | "complete" | "open";

function isComplete(review: CockpitReviewListItem): boolean {
  return review.runState === "completed";
}

export function ReviewIndex({ api }: { readonly api: CockpitApi }) {
  const [reviews, setReviews] = useState<readonly CockpitReviewListItem[]>([]);
  const [state, setState] = useState<"error" | "loading" | "ready">("loading");
  const [message, setMessage] = useState("");
  const [filter, setFilter] = useState<QueueFilter>("all");
  const [query, setQuery] = useState("");

  async function refresh() {
    setState("loading");
    try {
      setReviews((await api.reviews()).reviews);
      setState("ready");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
      setState("error");
    }
  }

  useEffect(() => {
    void refresh();
  }, [api]);

  const visibleReviews = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    return reviews.filter((review) => {
      const matchesFilter =
        filter === "all" ||
        (filter === "complete" && isComplete(review)) ||
        (filter === "open" && !isComplete(review));
      const matchesQuery =
        normalizedQuery.length === 0 ||
        review.declaredIntentSummary
          .toLocaleLowerCase()
          .includes(normalizedQuery) ||
        review.reviewSeriesId.toLocaleLowerCase().includes(normalizedQuery) ||
        review.headRevision.toLocaleLowerCase().includes(normalizedQuery);
      return matchesFilter && matchesQuery;
    });
  }, [filter, query, reviews]);

  const unanalyzedCount = reviews.filter(
    (review) => !isComplete(review),
  ).length;
  const blindSpotCount = reviews.reduce(
    (total, review) => total + review.blindSpotCount,
    0,
  );

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Reviews</h1>
          <p>
            Every change waiting on a human decision, newest activity first.
            Changes with unresolved gaps stay visible rather than being marked
            clean.
          </p>
        </div>
        <div className="page-head-actions">
          <button
            className="button secondary"
            onClick={() => void refresh()}
            type="button"
          >
            <RefreshCw size={16} aria-hidden="true" />
            Refresh
          </button>
        </div>
      </header>

      {state === "ready" && reviews.length > 0 ? (
        <div className="metric-row">
          <div className="metric">
            <span className="metric-label">In the queue</span>
            <span className="metric-value">{reviews.length}</span>
          </div>
          <div className={unanalyzedCount > 0 ? "metric attention" : "metric"}>
            <span className="metric-label">Awaiting analysis</span>
            <span className="metric-value">{unanalyzedCount}</span>
          </div>
          <div className={blindSpotCount > 0 ? "metric attention" : "metric"}>
            <span className="metric-label">Blind spots</span>
            <span className="metric-value">{blindSpotCount}</span>
          </div>
        </div>
      ) : null}

      <section className="queue-controls" aria-label="Filter reviews">
        <div
          className="segmented-control"
          role="group"
          aria-label="Review state"
        >
          {(
            [
              ["all", "All"],
              ["open", "Needs analysis"],
              ["complete", "Analyzed"],
            ] as const
          ).map(([value, label]) => (
            <button
              aria-pressed={filter === value}
              key={value}
              onClick={() => {
                setFilter(value);
              }}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>
        <label className="search-field">
          <Search size={17} aria-hidden="true" />
          <span className="sr-only">Search reviews</span>
          <input
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            placeholder="Search by title or revision"
            type="search"
            value={query}
          />
        </label>
      </section>

      {state === "loading" ? (
        <div
          className="skeleton-list"
          role="status"
          aria-label="Loading reviews"
        >
          <span className="skeleton-row" />
          <span className="skeleton-row" />
          <span className="skeleton-row" />
        </div>
      ) : state === "error" ? (
        <div className="callout error" role="alert">
          <CircleAlert size={19} aria-hidden="true" />
          <div>
            <strong>The review queue could not be loaded.</strong>
            <p>
              Nothing was lost. Refresh once the control plane is reachable
              again.
            </p>
            <details>
              <summary>Technical details</summary>
              <code>{message}</code>
            </details>
          </div>
        </div>
      ) : reviews.length === 0 ? (
        <div className="empty-state">
          <span className="empty-state-icon" aria-hidden="true">
            <Inbox size={22} />
          </span>
          <strong>Nothing is waiting for review</strong>
          <span>
            Connect a repository, then open or update a pull request. The change
            appears here with its evidence once analysis begins.
          </span>
          <a className="button primary" href="#/onboarding">
            Connect a repository
          </a>
        </div>
      ) : visibleReviews.length === 0 ? (
        <div className="empty-state compact">
          <strong>No changes match this view</strong>
          <span>Try another state, or clear the search.</span>
        </div>
      ) : (
        <section className="rows" aria-label="Reviews">
          {visibleReviews.map((review) => {
            const title = reviewTitle(review.declaredIntentSummary);
            const description = reviewDescription(review.declaredIntentSummary);
            const reference = reviewReference(review.reviewSeriesId);
            const href = `#/reviews/${encodeURIComponent(review.reviewSeriesId)}`;
            return (
              <article className="row queue-row" key={review.reviewSeriesId}>
                <div>
                  <h2>
                    <a className="row-title" href={href}>
                      {title}
                    </a>
                  </h2>
                  <div className="row-meta">
                    {reference === null ? null : (
                      <span className="repo">{reference}</span>
                    )}
                    <span
                      className="revision"
                      title={`Head revision ${review.headRevision}`}
                    >
                      {shortIdentifier(review.headRevision, 8)}
                    </span>
                    <span className="change-count">
                      <span className="addition">+{review.additions}</span>
                      <span className="deletion">
                        &minus;{review.deletions}
                      </span>
                    </span>
                  </div>
                  {description === null ? null : (
                    <p className="row-summary">{description}</p>
                  )}
                </div>
                <div className="row-signals">
                  <span
                    className={`state-badge tone-${stateTone(review.runState)}`}
                  >
                    {displayReviewRunState(review.runState)}
                  </span>
                  <span className="row-signal">
                    <strong>{review.findingCount}</strong>{" "}
                    {pluralize(review.findingCount, "finding")}
                  </span>
                  {review.blindSpotCount > 0 ? (
                    <span className="row-signal attention">
                      <strong>{review.blindSpotCount}</strong>{" "}
                      {pluralize(review.blindSpotCount, "blind spot")}
                    </span>
                  ) : null}
                </div>
                <span
                  className="row-time"
                  title={new Date(review.updatedAt).toLocaleString()}
                >
                  {relativeTime(review.updatedAt)}
                </span>
                <a
                  className="row-open"
                  href={href}
                  aria-label={`Open review: ${title}`}
                >
                  <ArrowRight size={17} aria-hidden="true" />
                </a>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
}
